// Procurement (docs/plans/phase2-procurement.md §3.3, §5): store and purchase requisitions,
// LPOs, GRN reversals, supplier invoices and statements. Every create carries a client
// request key so a retried request can't create the document twice.
import { queryOptions } from '@tanstack/react-query';
import {
  grnReversalSchema,
  grnSchema,
  grnSummarySchema,
  pageSchema,
  purchaseOrderSchema,
  purchaseOrderSummarySchema,
  purchaseRequisitionSchema,
  storeRequisitionSchema,
  supplierInvoiceSchema,
  supplierInvoiceSummarySchema,
  supplierStatementSchema,
  type createPurchaseOrderBodySchema,
  type createPurchaseRequisitionBodySchema,
  type createStoreRequisitionBodySchema,
  type createSupplierInvoiceBodySchema,
  type issueStoreRequisitionBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

// --- LPOs ------------------------------------------------------------------------------------

export const purchaseOrdersQuery = (status?: string) =>
  queryOptions({
    queryKey: ['procurement', 'orders', status ?? 'all'],
    queryFn: () =>
      api('GET', `/purchase-orders?limit=100${status === undefined ? '' : `&status=${status}`}`, {
        schema: pageSchema(purchaseOrderSummarySchema),
      }),
  });

export const purchaseOrderQuery = (id: string) =>
  queryOptions({
    queryKey: ['procurement', 'order', id],
    queryFn: () => api('GET', `/purchase-orders/${id}`, { schema: purchaseOrderSchema }),
  });

export const createPurchaseOrder = (body: In<typeof createPurchaseOrderBodySchema>) =>
  api('POST', '/purchase-orders', { body, schema: purchaseOrderSchema });

export type OrderAction = 'approve' | 'reject' | 'cancel' | 'close';

export const actOnPurchaseOrder = (id: string, action: OrderAction, reason: string) =>
  api('POST', `/purchase-orders/${id}/${action}`, {
    body: { reason },
    schema: purchaseOrderSchema,
  });

export const recordOrderPrinted = (id: string) => api('POST', `/purchase-orders/${id}/printed`);

// --- Purchase requisitions ------------------------------------------------------------------

export const purchaseRequisitionsQuery = queryOptions({
  queryKey: ['procurement', 'requisitions'],
  queryFn: () =>
    api('GET', '/purchase-requisitions?limit=100', {
      schema: z.array(purchaseRequisitionSchema),
    }),
});

export const purchaseRequisitionQuery = (id: string) =>
  queryOptions({
    queryKey: ['procurement', 'requisition', id],
    queryFn: () =>
      api('GET', `/purchase-requisitions/${id}`, { schema: purchaseRequisitionSchema }),
  });

export const createPurchaseRequisition = (body: In<typeof createPurchaseRequisitionBodySchema>) =>
  api('POST', '/purchase-requisitions', { body, schema: purchaseRequisitionSchema });

export const decidePurchaseRequisition = (
  id: string,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string,
) =>
  api('POST', `/purchase-requisitions/${id}/${decision}`, {
    body: { reason },
    schema: purchaseRequisitionSchema,
  });

// --- Supplier invoices and statements -------------------------------------------------------

export const supplierInvoicesQuery = (purchaseOrderId?: string) =>
  queryOptions({
    queryKey: ['procurement', 'invoices', purchaseOrderId ?? 'all'],
    queryFn: () =>
      api(
        'GET',
        `/supplier-invoices?limit=100${
          purchaseOrderId === undefined ? '' : `&purchaseOrderId=${purchaseOrderId}`
        }`,
        { schema: z.array(supplierInvoiceSummarySchema) },
      ),
  });

export const supplierInvoiceQuery = (id: string) =>
  queryOptions({
    queryKey: ['procurement', 'invoice', id],
    queryFn: () => api('GET', `/supplier-invoices/${id}`, { schema: supplierInvoiceSchema }),
  });

export const createSupplierInvoice = (body: In<typeof createSupplierInvoiceBodySchema>) =>
  api('POST', '/supplier-invoices', { body, schema: supplierInvoiceSchema });

export const decideSupplierInvoice = (
  id: string,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string | null,
) =>
  api('POST', `/supplier-invoices/${id}/${decision}`, {
    body: reason === null ? {} : { reason },
    schema: supplierInvoiceSchema,
  });

export const supplierStatementQuery = (supplierId: string, from: string, to: string) =>
  queryOptions({
    queryKey: ['procurement', 'statement', supplierId, from, to],
    queryFn: () =>
      api('GET', `/suppliers/${supplierId}/statement?from=${from}&to=${to}`, {
        schema: supplierStatementSchema,
      }),
  });

// --- Store requisitions ---------------------------------------------------------------------

export const storeRequisitionsQuery = (storeId: string, direction: 'from_store' | 'of_store') =>
  queryOptions({
    queryKey: ['stock', storeId, 'requisitions', direction],
    queryFn: () =>
      api('GET', `/store-requisitions?storeId=${storeId}&direction=${direction}&limit=100`, {
        schema: z.array(storeRequisitionSchema),
      }),
  });

export const storeRequisitionQuery = (id: string) =>
  queryOptions({
    queryKey: ['store-requisition', id],
    queryFn: () => api('GET', `/store-requisitions/${id}`, { schema: storeRequisitionSchema }),
  });

export const createStoreRequisition = (body: In<typeof createStoreRequisitionBodySchema>) =>
  api('POST', '/store-requisitions', { body, schema: storeRequisitionSchema });

export const issueStoreRequisition = (
  id: string,
  body: In<typeof issueStoreRequisitionBodySchema>,
) => api('POST', `/store-requisitions/${id}/issue`, { body, schema: storeRequisitionSchema });

export const decideStoreRequisition = (
  id: string,
  decision: 'close' | 'reject' | 'cancel',
  reason: string,
) =>
  api('POST', `/store-requisitions/${id}/${decision}`, {
    body: { reason },
    schema: storeRequisitionSchema,
  });

// --- Goods received notes and reversals -----------------------------------------------------

export const postedGrnsQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'grns', 'posted'],
    queryFn: () =>
      api('GET', `/goods-receipts?storeId=${storeId}&status=posted&limit=100`, {
        schema: pageSchema(grnSummarySchema),
      }),
  });

export const grnDetailQuery = (id: string) =>
  queryOptions({
    queryKey: ['grn', id],
    queryFn: () => api('GET', `/goods-receipts/${id}`, { schema: grnSchema }),
  });

export const grnReversalQuery = (id: string) =>
  queryOptions({
    queryKey: ['grn-reversal', id],
    queryFn: () => api('GET', `/goods-receipt-reversals/${id}`, { schema: grnReversalSchema }),
  });

export const requestGrnReversal = (grnId: string, reason: string) =>
  api('POST', `/goods-receipts/${grnId}/reversal`, {
    body: { reason },
    schema: grnReversalSchema,
  });

export const decideGrnReversal = (
  id: string,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string,
) =>
  api('POST', `/goods-receipt-reversals/${id}/${decision}`, {
    body: { reason },
    schema: grnReversalSchema,
  });
